import { useEffect, useState } from "react";
import { localDateTime } from "../../lib/datetime";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { packHistory, sendMonthlyPack } from "../../api/acctExport";
import { useApiError } from "../../hooks/useApiError";

const lastMonth = () => {
  const now = new Date();
  now.setMonth(now.getMonth() - 1);
  return now.toISOString().slice(0, 7);
};

// Monthly pack: one branded PDF with every statement, emailed to the company admins.
export const MonthlyPack = () => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [month, setMonth] = useState(lastMonth);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => {
    packHistory().then(setLog).catch(() => setLog([]));
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      const result = await sendMonthlyPack(month);
      toast.success(t("acct.pack.sent", { count: result.recipients.length }));
      setLog(await packHistory());
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard className="grid gap-3 p-4" data-testid="monthly-pack">
      <p className="label-xs">{t("acct.pack.title")}</p>
      <p className="text-xs text-muted-foreground">{t("acct.pack.hint")}</p>
      <div className="flex flex-wrap items-center gap-3">
        <NeuInput
          type="month"
          className="w-44"
          data-testid="pack-month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
        <NeuButton variant="primary" data-testid="pack-send" disabled={busy} onClick={send}>
          <Mail className="h-4 w-4" /> {busy ? t("acct.pack.sending") : t("acct.pack.send")}
        </NeuButton>
      </div>
      {log.length ? (
        <div className="grid gap-1" data-testid="pack-log">
          {log.slice(0, 4).map((row) => (
            <p key={`${row.month}-${row.at}`} className="text-[11px] text-muted-foreground">
              <span dir="ltr">{row.month}</span> · {t("acct.pack.recipients", { count: row.recipients.length })} ·{" "}
              <span dir="ltr">{localDateTime(row.at)}</span>
            </p>
          ))}
        </div>
      ) : null}
    </NeuCard>
  );
};
